import { AlertTriangle, Github, GitPullRequest, RefreshCw, Workflow, XOctagon } from 'lucide-react';
import { useGithubSummary } from '../hooks/useGithub';
import { StatCard } from '../components/jira/StatCard';
import { RepoCard } from '../components/github/RepoCard';
import { PullRequestList } from '../components/github/PullRequestList';
import { WorkflowRunList } from '../components/github/WorkflowRunList';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { getApiErrorMessage, isApiError } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { ComingSoon } from './ComingSoon';

export function Repositories() {
  const { data, isLoading, isFetching, isError, error, refetch } = useGithubSummary();

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (isApiError(error) && error.response.status === 503) {
    return <ComingSoon icon={Github} title="Repositories" integrationLabel="GitHub" />;
  }

  if (isError || !data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
        <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load GitHub data</p>
        <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(error)}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          Live from GitHub &middot; updated {formatRelativeTime(data.fetchedAt)}
        </p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-sm font-medium text-pulse-600 hover:text-pulse-700 dark:text-pulse-400"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Repositories" value={data.repos.length} icon={Github} index={0} />
        <StatCard label="Open pull requests" value={data.openPullRequests.length} icon={GitPullRequest} index={1} />
        <StatCard label="Open issues" value={data.totalOpenIssues} icon={AlertTriangle} tone="warning" index={2} />
        <StatCard label="Failing runs" value={data.failingRuns} icon={XOctagon} tone={data.failingRuns > 0 ? 'danger' : 'success'} index={3} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Open pull requests</CardTitle>
          </CardHeader>
          <CardBody>
            <PullRequestList pullRequests={data.openPullRequests} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              <Workflow size={14} /> Recent workflow runs
            </CardTitle>
          </CardHeader>
          <CardBody>
            <WorkflowRunList runs={data.recentWorkflowRuns} />
          </CardBody>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-500 dark:text-slate-400">Repositories</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.repos.map((repo, i) => (
            <RepoCard key={repo.id} repo={repo} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
